import { supabase } from '@/lib/supabase';
import { handleError, ErrorSeverity, ErrorContext } from '@/utils/errorHandler';
import { Resend } from 'resend';
import { v4 as uuidv4 } from 'uuid';
import { callFunction } from '@/utils/supabaseFunctions';
import { getMarketingUrl, getAppUrl } from '@/utils/domains';

// Constants from supabase.ts 
const SUPABASE_URL = "https://muoihzfomfercsujyyta.supabase.co";
const SUPABASE_ANON_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Im11b2loemZvbWZlcmNzdWp5eXRhIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDM4Nzc5ODYsImV4cCI6MjA1OTQ1Mzk4Nn0.yOlwlmjSRiqWhjEPWgCg_jvqLEgvfzQacDhezJlQZ98";

// Don't initialize Resend immediately to avoid errors if API key is missing
let resendInstance: Resend | null = null;

// Function to get or create the Resend instance
const getResend = (): Resend | null => {
  if (resendInstance) return resendInstance;
  
  // Check for API key in browser environment only
  const apiKey = import.meta.env?.VITE_RESEND_API_KEY;
    
  console.log('Attempting to initialize Resend with API key available:', !!apiKey);
  
  if (!apiKey) {
    console.warn('Resend API key not found in browser environment. Will use Edge Functions instead.');
    return null;
  }
  
  try {
    resendInstance = new Resend(apiKey);
    console.log('Resend initialized successfully');
    return resendInstance;
  } catch (error) {
    console.error('Failed to initialize Resend:', error);
    return null;
  }
};

export const EmailService = {
  /**
   * Send a password reset link to a user's email
   */
  async sendPasswordReset(email: string): Promise<{ success: boolean; error: string | null }> {
    const context: ErrorContext = { 
      operation: 'sendPasswordReset',
      additionalInfo: { email }
    };

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/set-password`,
      });

      if (error) throw error;
      return { success: true, error: null };
    } catch (error) {
      handleError(
        error, 
        "Failed to send password reset email", 
        ErrorSeverity.ERROR, 
        context
      );
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  },

  /**
   * Send an invitation email to a new user
   */
  async sendInvite(email: string, metadata: { organization_id: string; family_id: string; created_by?: string; firstName?: string; organizationName?: string; }): Promise<{ success: boolean; error: string | null; inviteLink?: string }> {
    const context: ErrorContext = { 
      operation: 'sendInvite',
      additionalInfo: { email, metadata }
    };

    try {
      console.log('Sending invitation email to:', email, 'with metadata:', metadata);
      
      // Generate a secure, unique token
      const token = uuidv4();
      const expiresAt = new Date(Date.now() + 1000 * 60 * 60 * 24 * 7).toISOString(); // 7 days from now

      // Get auth token for REST API call
      const { data: sessionData } = await supabase.auth.getSession();
      const authToken = sessionData?.session?.access_token;
      
      console.log('Auth token available:', !!authToken);

      if (!authToken) {
        console.error('No auth token available for parent invitation creation');
        throw new Error('Authentication required');
      }

      // Insert invitation record using direct REST API call to bypass TypeScript errors
      console.log('Creating parent invitation record...');
      const response = await fetch(
        `${SUPABASE_URL}/rest/v1/parent_invitations`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`,
            'apikey': SUPABASE_ANON_KEY
          },
          body: JSON.stringify({
            email,
            organization_id: metadata.organization_id,
            family_id: metadata.family_id,
            token,
            expires_at: expiresAt,
            created_by: metadata.created_by || null
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Error inserting parent invitation:', errorData);
        throw new Error('Failed to create invitation record');
      }
      
      console.log('Parent invitation record created successfully');

      // Build the invite link with the token - use marketing domain for initial password setup
      const siteUrl = getMarketingUrl();
      const inviteLink = `${siteUrl}/set-password?token=${encodeURIComponent(token)}`;
      console.log('Generated invitation link:', inviteLink);

      // Build the email content
      const subject = "You've been invited to join Hivetivity!";
      if (!metadata.organizationName) {
        console.warn('[EmailService] organizationName missing in invite metadata. Using fallback (\'our platform\'). Metadata:', metadata);
      }
      const html = `
<p>Hello ${metadata.firstName || ''},</p>
<p>You've been invited to join <b>${metadata.organizationName || 'our platform'}</b> as a parent.</p>
<p>
  <a href="${inviteLink}" style="background:#2563eb;color:#fff;padding:10px 20px;border-radius:5px;text-decoration:none;">Accept Invitation</a>
</p>
<p>If you did not expect this invitation, you can ignore this email.</p>
`;

      try {
        // Try to send the email using the edge function directly, bypassing the resend-config check
        try {
          const sendResult = await callFunction('send-parent-invite', {
            method: 'POST',
            body: {
              email,
              subject,
              html,
              metadata: {
                inviteLink,
                firstName: metadata.firstName,
                organizationName: metadata.organizationName
              }
            }
          });
          
          console.log('Email sent via edge function:', sendResult);
          
          return {
            success: true,
            error: null,
            inviteLink
          };
        } catch (sendError) {
          console.error('Error sending via edge function:', sendError);
          // Continue with fallback methods instead of returning immediately
        }
      } catch (configError) {
        console.warn('Failed to send email:', configError);
        // Continue with fallback methods
      }

      // Get the Resend instance for client-side sending as fallback
      const resend = getResend();
      
      // Check if Resend is properly configured
      if (!resend) {
        console.warn('Resend is not configured. Email will not be sent, but invitation record was created successfully.');
        return { 
          success: true, 
          error: 'Email sending is disabled but invitation was created. Share this link manually.',
          inviteLink
        };
      }

      // Send the email with Resend
      console.log('Sending email via Resend API...');
      const { error: resendError } = await resend.emails.send({
        from: 'Hivetivity <noreply@hivetivity.com>',
        to: email,
        subject,
        html,
      });

      if (resendError) {
        console.error('Resend invitation email error:', resendError);
        return {
          success: false,
          error: `Failed to send email: ${resendError.message}. Use this invitation link instead.`,
          inviteLink
        };
      }

      console.log('Invitation email sent successfully');
      return { 
        success: true, 
        error: null,
        inviteLink
      };
    } catch (error) {
      handleError(
        error, 
        "Failed to send invitation email", 
        ErrorSeverity.ERROR, 
        context
      );
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error',
        inviteLink: null
      };
    }
  },

  /**
   * Send an invoice email with payment link
   */
  async sendInvoiceEmail(
    invoice: { id: string; total_cents: number; due_date?: string; status?: string; invoice_number?: string; }, 
    familyEmail: string, 
    organizationName: string,
    paymentLink?: string,
    organizationLogoUrl?: string
  ): Promise<{ success: boolean; error: string | null }> {
    const context: ErrorContext = { 
      operation: 'sendInvoiceEmail',
      additionalInfo: { invoiceId: invoice.id, familyEmail, organizationName }
    };

    try {
      console.log('Sending invoice email to:', familyEmail, 'for invoice:', invoice.id);
      
      // Format the invoice amount
      const formattedAmount = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
      }).format(invoice.total_cents / 100);

      // Format the due date
      const formattedDueDate = invoice.due_date 
        ? new Date(invoice.due_date).toLocaleDateString('en-US', { 
            weekday: 'long', 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
          })
        : 'No due date specified';

      // Build the email content
      const subject = `Invoice from ${organizationName} - ${formattedAmount} Due`;
      
      const html = `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Invoice from ${organizationName}</title>
</head>
<body style="margin: 0; padding: 0; background-color: #f9f9f9; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;">
  <table align="center" cellpadding="0" cellspacing="0" width="100%" style="background-color: #f9f9f9; padding: 20px 0;">
    <tr>
      <td>
        <!-- Logo Header -->
        <table align="center" cellpadding="0" cellspacing="0" width="100%" style="max-width: 600px;">
          <tr>
            <td align="center" style="padding: 40px 20px;">
              <img src="${organizationLogoUrl || 'https://imagedelivery.net/ylmXutM6_iJT3CT9AqBdjQ/15521506-bf83-4c94-881f-2ed325767800/public'}" alt="${organizationName}" style="max-width: 180px; height: auto;">
            </td>
          </tr>
        </table>
        
        <!-- Main Content -->
        <table align="center" cellpadding="0" cellspacing="0" width="100%" style="max-width: 600px; background-color: #ffffff; border-radius: 8px; box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);">
          <tr>
            <td style="padding: 40px 30px;">
              <h1 style="color: #1f2937; font-size: 28px; font-weight: 600; margin: 0 0 10px 0; text-align: center;">
                Invoice from ${organizationName}
              </h1>
              
              <p style="color: #6b7280; font-size: 16px; line-height: 24px; margin: 0 0 30px 0; text-align: center;">
                Please review and pay your invoice below
              </p>
              
              <!-- Invoice Details Card -->
              <div style="background-color: #f8fafc; border-radius: 8px; padding: 30px; margin: 30px 0; border: 1px solid #e5e7eb;">
                <table width="100%" cellpadding="0" cellspacing="0">
                  <tr>
                    <td>
                      <div style="font-size: 32px; font-weight: bold; color: #1f2937; margin-bottom: 5px;">
                        ${formattedAmount}
                      </div>
                      <div style="color: #6b7280; font-size: 14px;">
                        Due: ${formattedDueDate}
                      </div>
                    </td>
                    <td align="right">
                      <span style="background-color: #fbbf24; color: #92400e; padding: 6px 16px; border-radius: 20px; font-size: 14px; font-weight: bold; text-transform: uppercase; display: inline-block;">
                        ${(invoice.status || 'unpaid').toUpperCase()}
                      </span>
                    </td>
                  </tr>
                </table>
                
                <div style="border-top: 1px solid #e5e7eb; padding-top: 20px; margin-top: 20px;">
                  <table style="width: 100%; font-size: 14px; color: #6b7280;">
                    <tr>
                      <td style="padding: 5px 0;">Invoice ID:</td>
                      <td style="padding: 5px 0; text-align: right; font-family: monospace; color: #4b5563;">
                        ${invoice.id.slice(-8)}
                      </td>
                    </tr>
                    ${invoice.invoice_number ? `
                    <tr>
                      <td style="padding: 5px 0;">Invoice Number:</td>
                      <td style="padding: 5px 0; text-align: right; color: #4b5563;">
                        ${invoice.invoice_number}
                      </td>
                    </tr>
                    ` : ''}
                  </table>
                </div>
              </div>
              
              ${paymentLink ? `
              <!-- Payment Button -->
              <table align="center" cellpadding="0" cellspacing="0" style="margin: 30px auto;">
                <tr>
                  <td align="center" style="background-color: #2563eb; border-radius: 6px;">
                    <a href="${paymentLink}" target="_blank" style="display: inline-block; padding: 14px 30px; font-size: 16px; color: #ffffff; text-decoration: none; border-radius: 6px;">
                      Pay Invoice Online
                    </a>
                  </td>
                </tr>
              </table>
              <p style="color: #6b7280; font-size: 12px; margin: -15px 0 0 0; text-align: center;">
                Secure payment powered by Stripe
              </p>
              ` : `
              <div style="text-align: center; margin: 30px 0; padding: 20px; background-color: #fef3c7; border-radius: 6px; border: 1px solid #f59e0b;">
                <p style="color: #92400e; margin: 0; font-weight: bold;">Online payment is not available</p>
                <p style="color: #92400e; margin: 5px 0 0 0; font-size: 14px;">Please contact us for payment options</p>
              </div>
              `}
              
              <!-- Questions Section -->
              <div style="background-color: #f3f4f6; border-radius: 6px; padding: 20px; margin-top: 30px;">
                <p style="color: #4b5563; font-size: 14px; margin: 0; line-height: 20px;">
                  <strong>Questions?</strong> Please don't hesitate to reach out if you have any questions about this invoice.
                </p>
              </div>
            </td>
          </tr>
        </table>
        
        <!-- Footer -->
        <table align="center" cellpadding="0" cellspacing="0" width="100%" style="max-width: 600px; margin-top: 40px;">
          <tr>
            <td align="center" style="padding: 20px; color: #666666; font-size: 14px; line-height: 20px;">
              <p style="margin: 0 0 10px 0;">
                This email was sent by ${organizationName}
              </p>
              <p style="margin: 0 0 10px 0;">
                Powered by <a href="${getMarketingUrl()}" style="color: #2563eb; text-decoration: none;">Hivetivity</a>
              </p>
              <p style="margin: 0; font-size: 12px; color: #999999;">
                © ${new Date().getFullYear()} Hivetivity. All rights reserved.
              </p>
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;

      try {
        // Try to send via edge function first
        const sendResult = await callFunction('send-parent-invite', {
          method: 'POST',
          body: {
            email: familyEmail,
            subject,
            html,
            metadata: {
              invoiceId: invoice.id,
              organizationName,
              paymentLink
            }
          }
        });
        
        console.log('Invoice email sent via edge function:', sendResult);
        
        return {
          success: true,
          error: null
        };
      } catch (sendError) {
        console.error('Error sending via edge function:', sendError);
        // Continue with fallback methods
      }

      // Get the Resend instance for client-side sending as fallback
      const resend = getResend();
      
      if (!resend) {
        console.warn('Resend is not configured. Invoice email will not be sent.');
        return { 
          success: false, 
          error: 'Email service is not configured'
        };
      }

      // Send the email with Resend
      console.log('Sending invoice email via Resend API...');
      const { error: resendError } = await resend.emails.send({
        from: 'Hivetivity <noreply@hivetivity.com>',
        to: familyEmail,
        subject,
        html,
      });

      if (resendError) {
        console.error('Resend invoice email error:', resendError);
        return {
          success: false,
          error: `Failed to send email: ${resendError.message}`
        };
      }

      console.log('Invoice email sent successfully');
      return { 
        success: true, 
        error: null
      };
    } catch (error) {
      handleError(
        error, 
        "Failed to send invoice email", 
        ErrorSeverity.ERROR, 
        context
      );
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  },
  
  /**
   * Send document request email to a parent
   */
  async sendDocumentRequestEmail(
    familyEmail: string,
    familyName: string,
    requestTitle: string,
    requestDescription: string | undefined,
    deadline: string | undefined,
    childrenNames: string[],
    uploadLink: string,
    organizationName: string,
    organizationLogoUrl?: string,
    signingMode?: 'upload' | 'form'
  ): Promise<{ success: boolean; error: string | null }> {
    const context: ErrorContext = { 
      operation: 'sendDocumentRequestEmail',
      additionalInfo: { familyEmail, requestTitle, organizationName }
    };

    try {
      console.log('Sending document request email to:', familyEmail, 'for request:', requestTitle);

      const childrenText = childrenNames && childrenNames.length > 0 
        ? ` for ${childrenNames.join(' and ')}` 
        : '';

      const deadlineText = deadline 
        ? `\n<p><strong style="color: #dc2626;">Deadline:</strong> ${new Date(deadline).toLocaleDateString('en-US', { 
            weekday: 'long', 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
          })}</p>` 
        : '';

      const subject = `Document Request: ${requestTitle}`;
      
      const html = `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Document Request from ${organizationName}</title>
</head>
<body style="margin: 0; padding: 0; background-color: #f9f9f9; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;">
  <table align="center" cellpadding="0" cellspacing="0" width="100%" style="background-color: #f9f9f9; padding: 20px 0;">
    <tr>
      <td>
        <!-- Logo Header -->
        <table align="center" cellpadding="0" cellspacing="0" width="100%" style="max-width: 600px;">
          <tr>
            <td align="center" style="padding: 20px;">
              <img src="${organizationLogoUrl || 'https://imagedelivery.net/ylmXutM6_iJT3CT9AqBdjQ/15521506-bf83-4c94-881f-2ed325767800/public'}" alt="${organizationName}" style="max-width: 150px; height: auto;">
            </td>
          </tr>
        </table>
        
        <!-- Main Content -->
        <table align="center" cellpadding="0" cellspacing="0" width="100%" style="max-width: 600px; background-color: #ffffff; border-radius: 8px; box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);">
          <tr>
            <td style="padding: 40px 30px;">
              <h1 style="color: #1f2937; font-size: 28px; font-weight: 600; margin: 0 0 10px 0;">
                Document Request
              </h1>
              
              <p style="color: #6b7280; font-size: 16px; line-height: 24px; margin: 0 0 30px 0;">
                Hello ${familyName} family,
              </p>
              
              <p style="color: #374151; font-size: 16px; line-height: 24px; margin: 0 0 20px 0;">
                We need you to submit the following document${childrenText}:
              </p>
              
              <!-- Document Details Card -->
              <div style="background-color: #f8fafc; border-radius: 8px; padding: 24px; margin: 24px 0; border: 1px solid #e5e7eb;">
                <h2 style="color: #1f2937; font-size: 20px; font-weight: 600; margin: 0 0 12px 0;">
                  ${requestTitle}
                </h2>
                ${requestDescription ? `
                <p style="color: #6b7280; font-size: 14px; line-height: 20px; margin: 0 0 16px 0;">
                  ${requestDescription}
                </p>
                ` : ''}
                ${deadlineText}
              </div>
              
              <!-- Action Button -->
              <table align="center" cellpadding="0" cellspacing="0" style="margin: 30px auto;">
                <tr>
                  <td align="center" style="background-color: #2563eb; border-radius: 6px;">
                    <a href="${uploadLink}" target="_blank" style="display: inline-block; padding: 14px 30px; font-size: 16px; color: #ffffff; text-decoration: none; border-radius: 6px; font-weight: 600;">
                      ${signingMode === 'form' ? 'Sign Document' : 'Upload Document'}
                    </a>
                  </td>
                </tr>
              </table>
              
              <p style="color: #6b7280; font-size: 14px; margin: 20px 0 0 0; text-align: center;">
                If you have any questions, please don't hesitate to contact us.
              </p>
              
              <!-- Alternative Link -->
              <div style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #e5e7eb;">
                <p style="color: #6b7280; font-size: 12px; margin: 0; text-align: center;">
                  Having trouble with the button? Copy and paste this link into your browser:
                </p>
                <p style="color: #2563eb; font-size: 12px; margin: 5px 0 0 0; text-align: center; word-break: break-all;">
                  ${uploadLink}
                </p>
              </div>
            </td>
          </tr>
        </table>
        
        <!-- Footer -->
        <table align="center" cellpadding="0" cellspacing="0" width="100%" style="max-width: 600px; margin-top: 20px;">
          <tr>
            <td align="center" style="padding: 20px; color: #666666; font-size: 12px; line-height: 16px;">
              <p style="margin: 0;">
                This email was sent by ${organizationName}
              </p>
              <p style="margin: 5px 0 0 0;">
                Powered by <a href="${getMarketingUrl()}" style="color: #2563eb; text-decoration: none;">Hivetivity</a>
              </p>
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;

      try {
        // Try to send via edge function first
        const sendResult = await callFunction('send-parent-invite', {
          method: 'POST',
          body: {
            email: familyEmail,
            subject,
            html,
            metadata: {
              requestTitle,
              familyName,
              organizationName,
              uploadLink
            }
          }
        });
        
        console.log('Document request email sent via edge function:', sendResult);
        
        return {
          success: true,
          error: null
        };
      } catch (sendError) {
        console.error('Error sending via edge function:', sendError);
        // Continue with fallback methods
      }

      // Get the Resend instance for client-side sending as fallback
      const resend = getResend();
      
      if (!resend) {
        console.warn('Resend is not configured. Document request email will not be sent.');
        return { 
          success: false, 
          error: 'Email service is not configured'
        };
      }

      // Send the email with Resend
      console.log('Sending document request email via Resend API...');
      const { error: resendError } = await resend.emails.send({
        from: 'Hivetivity <noreply@hivetivity.com>',
        to: familyEmail,
        subject,
        html,
      });

      if (resendError) {
        console.error('Resend document request email error:', resendError);
        return {
          success: false,
          error: `Failed to send email: ${resendError.message}`
        };
      }

      console.log('Document request email sent successfully');
      return { 
        success: true, 
        error: null
      };
    } catch (error) {
      handleError(
        error, 
        "Failed to send document request email", 
        ErrorSeverity.ERROR, 
        context
      );
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  },

  /**
   * Send document request reminder email
   */
  async sendDocumentRequestReminder(
    familyEmail: string,
    familyName: string,
    requestTitle: string,
    deadline: string | undefined,
    daysUntilDeadline: number,
    uploadLink: string,
    organizationName: string
  ): Promise<{ success: boolean; error: string | null }> {
    const context: ErrorContext = { 
      operation: 'sendDocumentRequestReminder',
      additionalInfo: { familyEmail, requestTitle, daysUntilDeadline }
    };

    try {
      const urgencyLevel = daysUntilDeadline <= 1 ? 'urgent' : daysUntilDeadline <= 3 ? 'important' : 'normal';
      
      let subjectPrefix = '';
      let urgencyMessage = '';
      
      if (urgencyLevel === 'urgent') {
        subjectPrefix = '🚨 URGENT: ';
        urgencyMessage = daysUntilDeadline === 0 
          ? '<p style="color: #dc2626; font-weight: bold; background-color: #fef2f2; padding: 12px; border-radius: 6px; border-left: 4px solid #dc2626;">⚠️ This document is due TODAY!</p>'
          : '<p style="color: #dc2626; font-weight: bold; background-color: #fef2f2; padding: 12px; border-radius: 6px; border-left: 4px solid #dc2626;">⚠️ This document is due tomorrow!</p>';
      } else if (urgencyLevel === 'important') {
        subjectPrefix = '⏰ Reminder: ';
        urgencyMessage = `<p style="color: #f59e0b; font-weight: bold; background-color: #fffbeb; padding: 12px; border-radius: 6px; border-left: 4px solid #f59e0b;">⏳ Only ${daysUntilDeadline} days left to submit!</p>`;
      } else {
        subjectPrefix = 'Reminder: ';
      }

      const subject = `${subjectPrefix}Document Request - ${requestTitle}`;

      const html = `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Document Request Reminder</title>
</head>
<body style="margin: 0; padding: 0; background-color: #f9f9f9; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;">
  <table align="center" cellpadding="0" cellspacing="0" width="100%" style="background-color: #f9f9f9; padding: 20px 0;">
    <tr>
      <td>
        <table align="center" cellpadding="0" cellspacing="0" width="100%" style="max-width: 600px; background-color: #ffffff; border-radius: 8px; box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);">
          <tr>
            <td style="padding: 40px 30px;">
              <h1 style="color: #1f2937; font-size: 28px; font-weight: 600; margin: 0 0 10px 0;">
                Document Request Reminder
              </h1>
              
              <p style="color: #6b7280; font-size: 16px; line-height: 24px; margin: 0 0 20px 0;">
                Hello ${familyName} family,
              </p>
              
              ${urgencyMessage}
              
              <p style="color: #374151; font-size: 16px; line-height: 24px; margin: 20px 0;">
                This is a friendly reminder that we still need you to submit: <strong>${requestTitle}</strong>
              </p>
              
              ${deadline ? `
              <div style="background-color: #f8fafc; border-radius: 8px; padding: 20px; margin: 20px 0; border-left: 4px solid #2563eb;">
                <p style="margin: 0; color: #1f2937; font-size: 16px;">
                  <strong>Deadline:</strong> ${new Date(deadline).toLocaleDateString('en-US', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </p>
              </div>
              ` : ''}
              
              <table align="center" cellpadding="0" cellspacing="0" style="margin: 30px auto;">
                <tr>
                  <td align="center" style="background-color: #2563eb; border-radius: 6px;">
                    <a href="${uploadLink}" target="_blank" style="display: inline-block; padding: 14px 30px; font-size: 16px; color: #ffffff; text-decoration: none; border-radius: 6px; font-weight: 600;">
                      Upload Document Now
                    </a>
                  </td>
                </tr>
              </table>
              
              <p style="color: #6b7280; font-size: 14px; margin: 20px 0 0 0; text-align: center;">
                Need help? Please contact us and we'll be happy to assist you.
              </p>
            </td>
          </tr>
        </table>
        
        <table align="center" cellpadding="0" cellspacing="0" width="100%" style="max-width: 600px; margin-top: 20px;">
          <tr>
            <td align="center" style="padding: 20px; color: #666666; font-size: 12px;">
              <p style="margin: 0;">This reminder was sent by ${organizationName}</p>
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;

      // Use the same sending logic as document request emails
      try {
        const sendResult = await callFunction('send-parent-invite', {
          method: 'POST',
          body: {
            email: familyEmail,
            subject,
            html,
            metadata: {
              requestTitle,
              familyName,
              organizationName,
              reminderType: urgencyLevel
            }
          }
        });
        
        return { success: true, error: null };
      } catch (sendError) {
        console.error('Error sending reminder via edge function:', sendError);
      }

      // Fallback to Resend
      const resend = getResend();
      if (!resend) {
        return { success: false, error: 'Email service is not configured' };
      }

      const { error: resendError } = await resend.emails.send({
        from: 'Hivetivity <noreply@hivetivity.com>',
        to: familyEmail,
        subject,
        html,
      });

      if (resendError) {
        return { success: false, error: `Failed to send reminder: ${resendError.message}` };
      }

      return { success: true, error: null };
    } catch (error) {
      handleError(error, "Failed to send document request reminder", ErrorSeverity.ERROR, context);
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  },

  /**
   * Send waitlist notification when a spot opens up
   */
  async sendWaitlistNotification(
    parentEmail: string,
    parentName: string,
    studentName: string,
    className: string,
    organizationName: string
  ): Promise<{ success: boolean; error: string | null }> {
    const context: ErrorContext = { 
      operation: 'sendWaitlistNotification',
      additionalInfo: { parentEmail, studentName, className }
    };

    try {
      console.log('Sending waitlist notification to:', parentEmail, 'for class:', className);

      const subject = `🎉 Spot available for ${studentName} - ${className}`;
      
      const siteUrl = getAppUrl(); // Parent portal is part of authenticated app
      const enrollmentLink = `${siteUrl}/parent-portal/classes`;
      
      const html = `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Spot Available - ${className}</title>
</head>
<body style="margin: 0; padding: 0; background-color: #f9f9f9; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;">
  <table align="center" cellpadding="0" cellspacing="0" width="100%" style="background-color: #f9f9f9; padding: 20px 0;">
    <tr>
      <td>
        <table align="center" cellpadding="0" cellspacing="0" width="100%" style="max-width: 600px; background-color: #ffffff; border-radius: 8px; box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);">
          <tr>
            <td style="padding: 40px 30px;">
              <div style="background-color: #10b981; color: white; padding: 20px; border-radius: 8px; text-align: center; margin-bottom: 20px;">
                <h1 style="margin: 0; font-size: 24px;">🎉 Great News!</h1>
                <p style="margin: 10px 0 0 0; font-size: 18px;">A spot has opened up!</p>
              </div>
              
              <p style="color: #374151; font-size: 16px; line-height: 24px;">Hi ${parentName},</p>
              
              <p style="color: #374151; font-size: 16px; line-height: 24px;">
                Fantastic news! A spot has opened up in <strong>${className}</strong> for <strong>${studentName}</strong>.
              </p>
              
              <div style="background-color: #fef3c7; border-left: 4px solid #f59e0b; padding: 16px; margin: 20px 0;">
                <h3 style="margin-top: 0; color: #92400e;">⚡ Act Fast!</h3>
                <p style="margin-bottom: 0; color: #92400e;">This spot won't last long. Enroll now to secure ${studentName}'s place in the class.</p>
              </div>
              
              <table align="center" cellpadding="0" cellspacing="0" style="margin: 30px auto;">
                <tr>
                  <td align="center" style="background-color: #2563eb; border-radius: 6px;">
                    <a href="${enrollmentLink}" target="_blank" style="display: inline-block; padding: 16px 32px; font-size: 16px; color: #ffffff; text-decoration: none; border-radius: 6px; font-weight: bold;">
                      🚀 Enroll Now
                    </a>
                  </td>
                </tr>
              </table>
              
              <p style="color: #6b7280; font-size: 14px; margin: 20px 0 0 0; text-align: center;">
                If you have any questions, please don't hesitate to contact us.
              </p>
              
              <p style="color: #374151; font-size: 16px; margin-top: 30px;">
                Best regards,<br>
                <strong>${organizationName}</strong>
              </p>
            </td>
          </tr>
        </table>
        
        <table align="center" cellpadding="0" cellspacing="0" width="100%" style="max-width: 600px; margin-top: 20px;">
          <tr>
            <td align="center" style="padding: 20px; color: #666666; font-size: 12px;">
              <p style="margin: 0;">This notification was automatically sent when a spot opened up</p>
              <p style="margin: 5px 0 0 0;">Powered by <a href="${getMarketingUrl()}" style="color: #2563eb;">Hivetivity</a></p>
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;

      try {
        // Send via your existing edge function
        const sendResult = await callFunction('send-parent-invite', {
          method: 'POST',
          body: {
            email: parentEmail,
            subject,
            html,
            metadata: {
              studentName,
              className,
              organizationName,
              type: 'waitlist_notification'
            }
          }
        });
        
        console.log('Waitlist notification sent via edge function:', sendResult);
        
        return {
          success: true,
          error: null
        };
      } catch (sendError) {
        console.error('Error sending via edge function:', sendError);
        // Continue with fallback
      }

      // Fallback to Resend client
      const resend = getResend();
      
      if (!resend) {
        console.warn('Resend is not configured. Waitlist notification will not be sent.');
        return { 
          success: false, 
          error: 'Email service is not configured'
        };
      }

      console.log('Sending waitlist notification via Resend API...');
      const { error: resendError } = await resend.emails.send({
        from: 'Hivetivity <noreply@hivetivity.com>',
        to: parentEmail,
        subject,
        html,
      });

      if (resendError) {
        console.error('Resend waitlist notification error:', resendError);
        return {
          success: false,
          error: `Failed to send email: ${resendError.message}`
        };
      }

      console.log('Waitlist notification sent successfully');
      return { 
        success: true, 
        error: null
      };
    } catch (error) {
      handleError(
        error, 
        "Failed to send waitlist notification", 
        ErrorSeverity.ERROR, 
        context
      );
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  },

  // For testing purposes only - don't use in production
  testResendConfiguration() {
    const resend = getResend();
    return {
      isConfigured: !!resend,
      apiKeyAvailable: !!import.meta.env?.VITE_RESEND_API_KEY
    };
  }
};
